"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Summary = { up: number; down: number; agreementRate: number; comments: { ts: string; vote: "up" | "down"; comment: string }[] };

export function ParkFeedback({ parkId, parkName }: { parkId: string; parkName: string }) {
  const [vote, setVote] = useState<"up" | "down" | null>(null);
  const [comment, setComment] = useState("");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/feedback?parkId=${encodeURIComponent(parkId)}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setSummary(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [parkId]);

  async function submit() {
    if (!vote) return;
    setSubmitting(true);
    setErr(null);
    try {
      const r = await fetch("/api/feedback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ parkId, vote, comment: comment.trim() || undefined }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSummary(d);
      setComment("");
      setVote(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Does this score feel accurate?</CardTitle>
        <CardDescription>
          Your read of {parkName} matters. We&rsquo;re testing whether the model lines up with how people
          actually use the park. Submissions are stored locally; no account needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={vote === "up" ? "default" : "outline"}
            onClick={() => setVote("up")}
            disabled={submitting}
            aria-pressed={vote === "up"}
          >
            👍 Feels right
          </Button>
          <Button
            variant={vote === "down" ? "default" : "outline"}
            onClick={() => setVote("down")}
            disabled={submitting}
            aria-pressed={vote === "down"}
          >
            👎 Doesn&rsquo;t match
          </Button>
          {summary && (
            <span className="text-xs text-muted-foreground ml-auto">
              {summary.up} 👍 · {summary.down} 👎
              {summary.up + summary.down > 0 && (
                <> · agreement {(summary.agreementRate * 100).toFixed(0)}%</>
              )}
            </span>
          )}
        </div>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional: tell us what the model misses or gets right…"
          rows={2}
          className="w-full border border-border rounded-md px-3 py-2 bg-card text-sm"
          disabled={submitting}
        />
        <div className="flex items-center gap-3">
          <Button onClick={submit} disabled={!vote || submitting}>
            {submitting ? "Sending…" : "Submit"}
          </Button>
          {err && <span className="text-xs text-clay-ink">Error: {err}</span>}
        </div>

        {summary && summary.comments.length > 0 && (
          <div className="pt-3 border-t border-border space-y-2">
            <p className="text-xs text-muted-foreground">Recent comments</p>
            {summary.comments.map((c, i) => (
              <p key={i} className="text-sm">
                <span className="mr-1">{c.vote === "up" ? "👍" : "👎"}</span>
                <span className="text-muted-foreground italic">&ldquo;{c.comment}&rdquo;</span>
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
